import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { HiMenu, HiX } from 'react-icons/hi';
import { FaCoins } from 'react-icons/fa';
import { useCoins } from '../hooks/useCoins.jsx';

const NAV = [
  { to: '/', label: 'Home', end: true },
  { to: '/minigames', label: 'Mini Games' },
  { to: '/quiz-rules', label: 'How to Play' },
  { to: '/contact', label: 'Contact' },
];

// Sticky top navigation. variant 'light' (bright site) | 'dark' (game stage).
export default function SiteHeader({ variant = 'light' }) {
  const { coins } = useCoins();
  const [open, setOpen] = useState(false);
  const dark = variant === 'dark';

  return (
    <header
      className={`sticky top-0 z-40 backdrop-blur-xl border-b ${
        dark
          ? 'bg-slate-950/70 border-white/10'
          : 'bg-white/80 border-violet-100'
      }`}
    >
      <div className="mx-auto max-w-site px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <img src="/icon.png" alt="QuizzyLoot" className="w-8 h-8 rounded-lg" />
          <span
            className={`text-lg font-black italic uppercase tracking-tight ${
              dark ? 'text-white' : 'text-slate-900'
            }`}
          >
            Quizzy<span className="text-gradient-brand">Loot</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-1">
          {NAV.map((n) => {
            const cls = `px-3 py-2 rounded-lg text-sm font-bold transition-colors ${
              dark ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-brand'
            }`;
            return n.external ? (
              <a key={n.to} href={n.to} className={cls}>
                {n.label}
              </a>
            ) : (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-lg text-sm font-bold transition-colors ${
                    isActive
                      ? 'text-brand'
                      : dark
                        ? 'text-slate-300 hover:text-white'
                        : 'text-slate-600 hover:text-brand'
                  }`
                }
              >
                {n.label}
              </NavLink>
            );
          })}
        </nav>

        {/* Right: coins + CTA + mobile toggle */}
        <div className="flex items-center gap-2 sm:gap-3">
          <span
            className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-black tabular-nums ${
              dark ? 'bg-white/10 text-amber-300' : 'bg-amber-brand/15 text-amber-600'
            }`}
          >
            <FaCoins className={dark ? 'text-amber-300' : 'text-amber-500'} /> {coins}
          </span>

          <Link
            to="/play"
            className="btn-shine hidden sm:inline-flex items-center rounded-full bg-brand hover:bg-brand-dark px-5 py-2 text-sm font-black uppercase tracking-wide text-white transition-colors shadow-lg shadow-brand/30"
          >
            Play Now
          </Link>

          <button
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
            className={`md:hidden p-2 rounded-lg ${
              dark ? 'text-white' : 'text-slate-700'
            }`}
          >
            {open ? <HiX size={24} /> : <HiMenu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <nav
          className={`md:hidden border-t px-4 py-3 space-y-1 ${
            dark ? 'border-white/10 bg-slate-950/95' : 'border-violet-100 bg-white'
          }`}
        >
          {NAV.map((n) => {
            const cls = `block px-3 py-2 rounded-lg text-sm font-bold ${
              dark ? 'text-slate-200' : 'text-slate-700'
            }`;
            return n.external ? (
              <a key={n.to} href={n.to} onClick={() => setOpen(false)} className={cls}>
                {n.label}
              </a>
            ) : (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `block px-3 py-2 rounded-lg text-sm font-bold ${
                    isActive ? 'text-brand' : dark ? 'text-slate-200' : 'text-slate-700'
                  }`
                }
              >
                {n.label}
              </NavLink>
            );
          })}
          <Link
            to="/play"
            onClick={() => setOpen(false)}
            className="block text-center rounded-full bg-brand px-5 py-2.5 text-sm font-black uppercase tracking-wide text-white"
          >
            Play Now
          </Link>
        </nav>
      )}
    </header>
  );
}
